/** localStorage key: user has opened AI assist settings at least once. */
export const STORAGE_KEY_AI_ASSIST_SETTINGS_SHOWN = "starmesh_ai_assist_settings_shown";

/**
 * True when performer config includes saved Drive URLs or uploaded context files.
 *
 * @param {Object|null|undefined} config - Performer config from GET /business/action-items/performer-config.
 * @returns {boolean}
 * @example
 * hasPerformerContextDetails({ drive_urls: ["https://docs.google.com/document/d/abc"] });
 * // true
 * @example
 * hasPerformerContextDetails({ drive_urls: [], context_files: [] });
 * // false
 */
export const hasPerformerContextDetails = (config) => {
  const driveUrls = Array.isArray(config?.drive_urls) ? config.drive_urls : [];
  const contextFiles = Array.isArray(config?.context_files) ? config.context_files : [];
  return driveUrls.some((url) => String(url || "").trim()) || contextFiles.length > 0;
};

/**
 * @returns {boolean} Whether AI assist settings were shown before on this browser.
 * @example
 * isAiAssistSettingsShown(); // false (first visit)
 */
export const isAiAssistSettingsShown = () => {
  try {
    return localStorage.getItem(STORAGE_KEY_AI_ASSIST_SETTINGS_SHOWN) === "1";
  } catch {
    return false;
  }
};

/**
 * Persist that AI assist settings were shown (subsequent runs are not gated).
 *
 * @example
 * markAiAssistSettingsShown();
 * isAiAssistSettingsShown(); // true
 */
export const markAiAssistSettingsShown = () => {
  try {
    localStorage.setItem(STORAGE_KEY_AI_ASSIST_SETTINGS_SHOWN, "1");
  } catch {
    // ignore quota / private mode
  }
};

/** localStorage: cached recipient profile text (web). */
export const RECIPIENT_PROFILE_CACHE_KEY = "business_recipient_profile_cache";

/**
 * True when a saved recipient profile is cached locally.
 *
 * @example
 * hasCachedRecipientProfile(); // true after saving profile in settings
 */
export const hasCachedRecipientProfile = () => {
  try {
    const cached = localStorage.getItem(RECIPIENT_PROFILE_CACHE_KEY);
    return Boolean(String(cached || "").trim());
  } catch {
    return false;
  }
};

/**
 * Clear AI-assist "settings shown" flag so first-time gate runs again.
 *
 * @example
 * resetAiAssistSettingsShown();
 * isAiAssistSettingsShown(); // false
 */
export const resetAiAssistSettingsShown = () => {
  try {
    localStorage.removeItem(STORAGE_KEY_AI_ASSIST_SETTINGS_SHOWN);
  } catch {
    // ignore
  }
};

/**
 * Clear local onboarding flags (AI settings shown, cached profile).
 *
 * @example
 * resetPerformerOnboardingLocalState();
 */
export const resetPerformerOnboardingLocalState = () => {
  resetAiAssistSettingsShown();
  try {
    localStorage.removeItem(RECIPIENT_PROFILE_CACHE_KEY);
  } catch {
    // ignore
  }
};

/**
 * True when first-time users without context should be sent to AI settings before running.
 *
 * Retries (Try again / refresh on a task that already has a performer result)
 * never redirect — the user already used AI; send them back through the run.
 *
 * @param {Object|null|undefined} config - Performer config snapshot.
 * @param {{ isRetry?: boolean }} [options]
 * @returns {boolean}
 * @example
 * shouldOpenSettingsBeforeRun({ drive_urls: [] }); // true if settings never shown
 * @example
 * shouldOpenSettingsBeforeRun({ drive_urls: ["https://..."], context_files: [] }); // false
 * @example
 * shouldOpenSettingsBeforeRun({ drive_urls: [] }, { isRetry: true }); // false
 */
export const shouldOpenSettingsBeforeRun = (config, { isRetry = false } = {}) => {
  if (isRetry) return false;
  return !hasPerformerContextDetails(config) && !isAiAssistSettingsShown();
};
